import { useState } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Box } from '@machobear/design-system'
import { confirm } from '@/components/dialog'
import { FieldDropdown, Group } from '@/components/form'
import { show as showImageEditModal } from '@/components/image-edit-modal'
import { showImageModal } from '@/components/image-modal'
import { showPdfModal } from '@/components/pdf-modal'
import UploadButton from '@/components/upload-button'
import ENV_VARS from '@/config'
import getLabels from '@/features/Profile/EmploymentInfo/labels'
import useMediaQuery from '@/hooks/use-media-query'
import { colors, fontWeights, fontSizes, breakpoints } from '@/styles'
import fileAsDataUrl from '@/utils/file-as-data-url'

const { BACKEND_SERVER } = ENV_VARS

const StyledGroup = styled(Group)`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  padding: 0;
  margin: 0;
`

const ViewButtonWrapper = styled.div`
  margin-top: ${props => (props.noLabel ? 0 : 10)}px;
  display: flex;
  flex: 1;
  align-self: center;
  justify-content: flex-end;
  align-items: center;
`

const PanelText = styled.p`
  font-size: ${fontSizes.regular};
  color: ${colors.regular};
  margin: 10px 0;
  padding-left: 15px;
`

const DeleteButton = styled.button`
  color: ${colors.red};
  background-color: ${colors.white};
  border: none;
  margin-right: 10px;
  font-size: ${fontSizes.small};
  outline: none;
  cursor: pointer;
  :active {
    opacity: 0.5;
  }
  @media (max-width: ${breakpoints.phoneMax}) {
    margin-right: 5px;
  }
`

const ViewButton = styled.p`
  padding: 8px 18px;
  margin: 0px;
  border-radius: 2px;
  text-align: center;
  font-size: 12px;
  min-width: 83px;
  border: 2px solid ${colors.regular};
  cursor: pointer;
  :hover {
    border-color: ${colors.secondary};
    color: ${colors.secondary};
  }
  :active {
    opacity: 0.5;
  }
  @media (max-width: ${breakpoints.phoneMax}) {
    padding: 8px 10px;
    min-width: 65px;
  }
`

const UploadWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  border: 1.5px solid ${colors.grey88};
  border-radius: 6px;
  height: 52px;
  width: 100%;
`

const ProvideText = styled.p`
  padding-left: 10px;
`

const StyledUploadButton = styled(UploadButton)`
  min-width: 85px;
  width: 85px;
  align-self: flex-end;
  background: none;
  color: ${colors.link};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.regular};
  border: none;
  height: 40px;

  :hover {
    color: ${colors.link};
    border: none;
  }
`

const CurrencyModalText = styled.span`
  display: block;
  padding: 0 40px;

  span:not(:last-child) {
    display: block;
    margin-bottom: 15px;
  }

  a {
    color: ${colors.link};
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    padding: 0;
  }
`

const Required = styled.span`
  color: ${colors.red64};
`

const IncomeVerificationDocumentButton = ({ setFieldValue, incomeVerification, isIncomePdf }) => {
  const { t } = useTranslation()
  return (
    <ViewButtonWrapper>
      <DeleteButton type="button" onClick={() => setFieldValue('income_verification', '')}>
        {t('r.profile.edit.credit_check_delete_doc.button', 'delete')}
      </DeleteButton>
      <ViewButton
        onClick={() => {
          isIncomePdf
            ? showPdfModal({ src: incomeVerification })
            : showImageModal({ src: incomeVerification })
        }}
      >
        {t('r.profile.edit.credit_check_view_doc.button', 'View')}
      </ViewButton>
    </ViewButtonWrapper>
  )
}

const UploadIncomeForm = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { values, setFieldValue } = useFormikContext()
  const [isIncomePdf, setIncomePdf] = useState(
    values.income_verification?.content_type === 'application/pdf'
  )
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const colSize = displayMobile ? 12 : 6

  const LABELS = getLabels(t)

  const incomeFile = values.income_verification

  const handleDeleteClick = ({ fileId, tag }) => {
    return confirm(
      t('r.profile.edit.delete_document.title', 'Delete Document?'),
      t(
        'r.profile.edit.delete_document_confirmation.text',
        'Are you sure you want to delete this document?'
      )
    ).then(async () => {
      await dispatch.livScore.deleteFile({
        fileId,
        tag
      })
      setFieldValue(tag, '')
      return
    })
  }

  const handleUploadClick = async () => {
    await confirm(
      t('r.profile.edit.foreign_currency_modal.title', 'Foreign Currency'),
      <Trans i18nKey="r.profile.edit.foreign_currency_modal.text">
        <CurrencyModalText>
          <span>
            If your document is in any currency aside from US or Canadian Dollars (CAD), please be
            sure to translate your document with all the exchange rates marked in CAD. If you do
            not, your documents cannot be approved.
          </span>
          <span>
            To obtain current exchange rates, you can visit{' '}
            <a href="https://www.xe.com/" target="_blank" rel="noopener noreferrer">
              xe.com by clicking here.
            </a>
          </span>
          <span>If you have any questions or concerns, please contact our support team.</span>
        </CurrencyModalText>
      </Trans>,
      {
        icon: 'money_yellow',
        okLabel: t('r.profile.edit.i_acknowledge.title', 'I Acknowledge'),
        hideCancelButton: true
      }
    )
  }

  const documentTypeOptions = [
    {
      label: t('r.profile.view.t4.text', 'T4'),
      value: {
        tag: 'income_verification',
        type: 't4'
      }
    },
    {
      label: t('r.profile.view.tax_slip.text', 'Tax Slips'),
      value: {
        tag: 'income_verification',
        type: 'tax_slip'
      }
    },
    {
      label: t('r.profile.view.pay_slip.text', 'Pay Slip'),
      value: {
        tag: 'income_verification',
        type: 'payslip'
      }
    },
    {
      label: t('r.profile.view.student_loans.text', 'Student Loans'),
      value: {
        tag: 'income_verification',
        type: 'student_loan'
      }
    },
    {
      label: t('r.profile.view.bank_statement.text', 'Bank Statement'),
      value: {
        tag: 'income_verification',
        type: 'bank_statement'
      }
    },
    {
      label: t('r.profile.view.job_offer_letter.text', 'Job Offer Letter'),
      value: {
        tag: 'income_verification',
        type: 'job_offer'
      }
    }
  ]

  return (
    <>
      {!incomeFile.id && (
        <Box display="flex" flexDirection={{ mobile: 'column', tablet: 'row' }}>
          <Box
            width="full"
            marginRight={{ mobile: 'none', tablet: 'large' }}
            marginBottom={{ mobile: 'small', tablet: 'none' }}
            style={{ flex: 1 }}
          >
            <FieldDropdown
              id="income_document_type"
              name="income_document_type"
              placeholder={t('r.profile.edit.liv_score_upload_verified_id.label', 'Document type')}
              options={documentTypeOptions}
              required
            />
          </Box>
          {incomeFile ? (
            <IncomeVerificationDocumentButton
              setFieldValue={setFieldValue}
              incomeVerification={values?.income_verification}
              isIncomePdf={isIncomePdf}
            />
          ) : (
            <Box width="full" style={{ flex: 1 }}>
              <UploadWrapper>
                <ProvideText>
                  {t('b.components.dropdown.photo_of_doc.placeholder', 'Photo of document')}
                  <Required>*</Required>
                </ProvideText>
                <StyledUploadButton
                  showError={false}
                  id="income_upload_input"
                  data-cy="upload_income_document"
                  name="income_verification"
                  accept={{
                    'image/png': ['.png'],
                    'image/jpg': ['.jpg', '.jpeg'],
                    'image/jpeg': ['.jpg', '.jpeg'],
                    'application/pdf': ['.pdf']
                  }}
                  onUploadClick={handleUploadClick}
                  onDropAccepted={async files => {
                    setIncomePdf(files[0].type === 'application/pdf')
                    showImageEditModal({
                      pdfState: files[0].type === 'application/pdf',
                      initialImageSrc: await fileAsDataUrl(files[0]),
                      onSave: imageDataUrl => setFieldValue('income_verification', imageDataUrl)
                    })
                  }}
                  theme="text"
                  disabled={!values?.income_document_type.tag}
                  floatError
                />
              </UploadWrapper>
            </Box>
          )}
        </Box>
      )}
      {incomeFile.id && (
        <Box display="flex" flexDirection={{ mobile: 'column', tablet: 'row' }}>
          <StyledGroup col={colSize}>
            <PanelText>{LABELS[incomeFile.document_type]}</PanelText>
          </StyledGroup>
          <Group col={colSize} bottom={0}>
            <ViewButtonWrapper noLabel>
              <DeleteButton
                type="button"
                onClick={() =>
                  handleDeleteClick({
                    fileId: incomeFile.id,
                    tag: incomeFile.tag
                  })
                }
              >
                {t('r.profile.edit.credit_check_delete_doc.button', 'delete')}
              </DeleteButton>
              <ViewButton
                onClick={() =>
                  isIncomePdf
                    ? showPdfModal({
                        src: `${BACKEND_SERVER}/secure/${incomeFile?.tag}/${incomeFile.id}/form.png`
                      })
                    : showImageModal({
                        src: `${BACKEND_SERVER}/secure/${incomeFile?.tag}/${incomeFile.id}/form.png`
                      })
                }
              >
                {t('r.profile.edit.credit_check_view_doc.button', 'View')}
              </ViewButton>
            </ViewButtonWrapper>
          </Group>
        </Box>
      )}
    </>
  )
}

export default UploadIncomeForm
